import {
  Button,
  Inline,
  notifySuccess,
  Stack,
  useModal,
} from '@omnipresentgroup/design-system';
import { useEmployeeCompensationMutation } from '@omnipresentgroup/domains';
import { Form, FormContext } from '@omnipresentgroup/flexible-forms';
import { useContext, useEffect, useState } from 'react';

import { AddSalaryChangeReviewModal, FormSkeleton } from './components';
import { useSalaryChangeSchema } from './hooks';
import type { SalaryChangeFormFields } from './types';

const FORM_ID = 'salary-change-form';

export const AddSalaryChangeForm = ({
  employeeId,
  onSuccess,
  showEmployeeTypeNotification,
}: {
  employeeId: string;
  onSuccess?: () => void;
  showEmployeeTypeNotification?: boolean;
}) => {
  const [formValues, setFormValues] = useState<SalaryChangeFormFields>();

  const { setErrors } = useContext(FormContext);

  const { modalProps } = useModal({});

  const [modalOpen, setModalOpen] = useState(false);

  const {
    schema: flexibleSchema,
    uiSchema: flexibleUiSchema,
    isLoading: isLoadingSchema,
  } = useSalaryChangeSchema({ employeeId });

  const {
    mutate: updateCompensation,
    isPending: isUpdatingCompensation,
    isSuccess: updateCompensationSuccess,
  } = useEmployeeCompensationMutation({
    employeeId,
  });

  // Reset form values when employeeId changes
  useEffect(() => {
    // eslint-disable-next-line unicorn/no-useless-undefined
    setFormValues(undefined);
    setErrors([]);
  }, [employeeId, setErrors]);

  useEffect(() => {
    if (updateCompensationSuccess) {
      notifySuccess({
        title: 'Salary change has been submitted successfully',
      });
      setModalOpen(false);
      onSuccess?.();
    }
  }, [onSuccess, updateCompensationSuccess]);

  return isLoadingSchema ? (
    <FormSkeleton />
  ) : (
    <Stack gap="32">
      <Form<SalaryChangeFormFields>
        id={FORM_ID}
        schema={flexibleSchema}
        uiSchema={flexibleUiSchema}
        formData={formValues}
        onChange={({ formData }) => setFormValues(formData)}
        onSubmit={({ formData }) => {
          setFormValues(formData);
          setModalOpen(true);
        }}
        readonly={!employeeId}
      />
      <Inline justify="flex-end">
        <Button
          type="submit"
          form={FORM_ID}
          disabled={!employeeId || isUpdatingCompensation}
        >
          Next
        </Button>
      </Inline>
      {formValues && (
        <AddSalaryChangeReviewModal
          employeeId={employeeId}
          formValues={formValues}
          modalProps={{
            ...modalProps,
            open: modalOpen,
            onOpenChange: setModalOpen,
          }}
          handleCancel={() => setModalOpen(false)}
          handleSubmit={() => {
            if (formValues) {
              updateCompensation({
                reason: formValues.reason,
                amount: Number(formValues.newAnnualGrossSalary.amount),
                currency: formValues.newAnnualGrossSalary.currency,
                effectiveDate: formValues.effectiveDate,
                hoursOfWork: formValues.weeklyWorkingHours,
                jobTitle: formValues.jobTitle,
                jobDescription: formValues.jobDescription,
                notes: formValues.additionalNotes,
                enabledNotifications: formValues.enabledNotifications,
              });
            }
          }}
          loading={isUpdatingCompensation}
          showEmployeeTypeNotification={showEmployeeTypeNotification}
        />
      )}
    </Stack>
  );
};
